import { AntDesign } from "@expo/vector-icons";
import { ProductThumbnail, Text } from "@towersystems/roam-common-native";
import { useThemeProvider } from "@towersystems/roam-common-native/lib/utilities/theme";
import { Product } from "@towersystems/roam-common/lib/generated-types";
import { TouchableOpacity } from "react-native";
import { Spacings, View } from "react-native-ui-lib";
import { toTitleCase } from "../../utilities/to-title-case";
import { lowestPrice } from "../../utilities/product";
import { moneyToFormattedString } from "../../utilities/money";

type ProductWithImageCount<T extends Product> = T & { imageCount?: number };

export type ProductListItemProps<T extends Product> = {
  product: ProductWithImageCount<T>;
  variants?: T[];
  action?: () => void;
  actionLabel?: string;
  paddingVertical?: number;
};

export function ProductListItem<T extends Product>({
  product,
  variants = [],
  action,
  actionLabel,
  paddingVertical,
}: ProductListItemProps<T>) {
  const { colors } = useThemeProvider();
  const imageCount = product.imageCount;

  function getImageUrl(product: T) {
    return product.image?.path;
  }

  const image = getImageUrl(product);

  return (
    <TouchableOpacity
      onPress={action}
      style={{
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "row",
        paddingVertical: paddingVertical,
      }}
    >
      <View row centerV style={{ flexBasis: "100%", flexShrink: 1 }}>
        <View style={{ marginRight: Spacings.s4 }}>
          <ProductThumbnail size="large" imageUrl={image} />
        </View>
        <View style={{ flexShrink: 1 }}>
          <View>
            <Text
              variant="heading"
              fontWeight="medium"
              numberOfLines={1}
              ellipsizeMode="head"
            >
              {toTitleCase(product.name)}
            </Text>
            {product.displayName ? (
              <Text color="subdued" variant="heading5">
                {product.displayName}
              </Text>
            ) : undefined}
          </View>
          {product.hasVariants ? (
            <View>
              <Text
                color="subdued"
                variant="heading5"
              >{`${product.totalVariants} Variants`}</Text>
            </View>
          ) : (
            <View>
              <Text color="subdued" variant="heading5">
                {product.stockNumber}
              </Text>
            </View>
          )}
          {imageCount !== undefined && (
            <View>
              <Text
                color={imageCount === 0 ? "destructive" : "subdued"}
                variant="heading5"
              >
                {imageCount === 0 ? "No photos" : `${imageCount} photo${imageCount === 1 ? "" : "s"}`}
              </Text>
            </View>
          )}
        </View>
      </View>
      <View style={{ paddingLeft: Spacings.s6 }} row centerV>
        <AntDesign
          style={[{ marginLeft: Spacings.s4 }]}
          name="right"
          size={24}
          color={colors.text}
        />
      </View>
    </TouchableOpacity>
  );
}
